import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { 
  Plus, Trash2, GripVertical, Type, FileText, Image, 
  ChevronDown, CheckSquare, Pencil, Eye, ArrowUp, ArrowDown
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

const FIELD_TYPES = {
  text_short: { icon: Type, label: { es: 'Texto Corto', en: 'Short Text' } },
  text_long: { icon: FileText, label: { es: 'Texto Largo', en: 'Long Text' } },
  document: { icon: FileText, label: { es: 'Documento', en: 'Document' } },
  image: { icon: Image, label: { es: 'Imagen', en: 'Image' } },
  select: { icon: ChevronDown, label: { es: 'Selector', en: 'Dropdown' } },
  checkbox: { icon: CheckSquare, label: { es: 'Casilla', en: 'Checkbox' } }
};

const DEFAULT_FORM_TEMPLATE = [
  {
    id: 'startup_name',
    type: 'text_short',
    label: { es: 'Nombre del Startup', en: 'Startup Name' },
    placeholder: { es: 'Ej: Mi Startup', en: 'Ex: My Startup' },
    required: true,
    order: 0
  },
  {
    id: 'founder_name',
    type: 'text_short',
    label: { es: 'Nombre del Fundador', en: 'Founder Name' },
    placeholder: { es: 'Nombre completo', en: 'Full name' },
    required: true,
    order: 1
  },
  {
    id: 'email',
    type: 'text_short',
    label: { es: 'Email', en: 'Email' },
    placeholder: { es: 'correo@ejemplo.com', en: 'email@example.com' },
    required: true,
    order: 2
  },
  {
    id: 'phone',
    type: 'text_short',
    label: { es: 'Teléfono', en: 'Phone' },
    placeholder: { es: '+56 9 1234 5678', en: '+1 234 567 8900' },
    required: true,
    order: 3
  },
  {
    id: 'website',
    type: 'text_short',
    label: { es: 'Sitio Web', en: 'Website' },
    placeholder: { es: 'https://www.ejemplo.com', en: 'https://www.example.com' },
    required: false,
    order: 4
  },
  {
    id: 'description',
    type: 'text_long',
    label: { es: 'Descripción del Startup', en: 'Startup Description' },
    placeholder: { es: 'Describe tu startup, el problema que resuelve y tu solución...', en: 'Describe your startup, the problem you solve and your solution...' },
    required: true,
    order: 5
  },
  {
    id: 'stage',
    type: 'select',
    label: { es: 'Etapa del Startup', en: 'Startup Stage' },
    required: true,
    options: [
      { value: 'idea', label: { es: 'Idea', en: 'Idea' } },
      { value: 'prototype', label: { es: 'Prototipo', en: 'Prototype' } },
      { value: 'mvp', label: { es: 'MVP', en: 'MVP' } },
      { value: 'early-revenue', label: { es: 'Ingresos Tempranos', en: 'Early Revenue' } },
      { value: 'growth', label: { es: 'Crecimiento', en: 'Growth' } }
    ],
    order: 6
  },
  {
    id: 'team_size',
    type: 'text_short',
    label: { es: 'Tamaño del Equipo', en: 'Team Size' },
    placeholder: { es: 'Número de personas', en: 'Number of people' },
    required: true,
    order: 7
  },
  {
    id: 'funding_raised',
    type: 'text_short',
    label: { es: 'Financiamiento Recaudado (USD)', en: 'Funding Raised (USD)' },
    placeholder: { es: '0', en: '0' },
    required: false,
    order: 8
  },
  {
    id: 'pitch_deck',
    type: 'document',
    label: { es: 'Pitch Deck', en: 'Pitch Deck' },
    helpText: { es: 'PDF, DOC, DOCX, PPT, PPTX (máx. 20MB)', en: 'PDF, DOC, DOCX, PPT, PPTX (max 20MB)' },
    acceptedFileTypes: ['.pdf', '.doc', '.docx', '.ppt', '.pptx'],
    required: false,
    order: 9
  }
];

const FormBuilder = ({ fields, onChange, language = 'es', scoringCriteria = [] }) => {
  const [editingField, setEditingField] = useState(null);
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [newFieldForm, setNewFieldForm] = useState({
    type: 'text_short',
    label: '',
    placeholder: '',
    helpText: '',
    required: false,
    options: []
  });
  const [newOption, setNewOption] = useState('');

  const t = {
    es: {
      title: 'Campos Personalizados',
      subtitle: 'Agrega nuevos campos que necesites solicitar al postulante',
      addField: 'Agregar Campo',
      editField: 'Editar Campo',
      fieldType: 'Tipo de Campo',
      fieldLabel: 'Nombre del Campo',
      placeholder: 'Placeholder',
      helpText: 'Texto de Ayuda',
      required: 'Obligatorio',
      options: 'Opciones',
      addOption: 'Agregar Opción',
      optionLabel: 'Etiqueta de Opción',
      save: 'Guardar',
      cancel: 'Cancelar',
      delete: 'Eliminar',
      preview: 'Vista Previa',
      loadTemplate: 'Cargar Plantilla',
      clearAll: 'Limpiar Todo',
      noFields: 'No hay campos configurados',
      dragToReorder: 'Arrastra para reordenar',
      acceptedFiles: 'Tipos de Archivo Aceptados',
      fixedFields: 'Campos Fijos (Email del Postulante)',
      customFields: 'Campos Personalizados'
    },
    en: {
      title: 'Custom Fields',
      subtitle: 'Add new fields you need to request from applicants',
      addField: 'Add Field',
      editField: 'Edit Field',
      fieldType: 'Field Type',
      fieldLabel: 'Field Name',
      placeholder: 'Placeholder',
      helpText: 'Help Text',
      required: 'Required',
      options: 'Options',
      addOption: 'Add Option',
      optionLabel: 'Option Label',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      preview: 'Preview',
      loadTemplate: 'Load Template',
      clearAll: 'Clear All',
      noFields: 'No fields configured',
      dragToReorder: 'Drag to reorder',
      acceptedFiles: 'Accepted File Types',
      fixedFields: 'Fixed Fields (Applicant Email)',
      customFields: 'Custom Fields'
    }
  }[language];

  const getLocalizedValue = (value) => {
    if (typeof value === 'object' && value !== null) {
      return value[language] || value.es || value.en || '';
    }
    return value || '';
  };

  const handleAddField = () => {
    setEditingField(null);
    setNewFieldForm({
      type: 'text_short',
      label: '',
      placeholder: '',
      helpText: '',
      required: false,
      options: []
    });
    setFieldDialogOpen(true);
  };

  const handleEditField = (field) => {
    setEditingField(field);
    setNewFieldForm({
      type: field.type,
      label: getLocalizedValue(field.label),
      placeholder: getLocalizedValue(field.placeholder),
      helpText: getLocalizedValue(field.helpText),
      required: field.required || false,
      options: field.options ? field.options.map(opt => ({
        value: opt.value,
        label: getLocalizedValue(opt.label)
      })) : [],
      acceptedFileTypes: field.acceptedFileTypes || []
    });
    setFieldDialogOpen(true);
  };

  const handleSaveField = () => {
    const fieldData = {
      id: editingField?.id || uuidv4(),
      type: newFieldForm.type,
      label: newFieldForm.label,
      placeholder: newFieldForm.placeholder || null,
      helpText: newFieldForm.helpText || null,
      required: newFieldForm.required,
      order: editingField?.order ?? fields.length,
      options: newFieldForm.type === 'select' ? newFieldForm.options.map(opt => ({
        value: opt.value || opt.label.toLowerCase().replace(/\s+/g, '_'),
        label: opt.label
      })) : null,
      acceptedFileTypes: ['document', 'image'].includes(newFieldForm.type) 
        ? (newFieldForm.type === 'document' 
            ? ['.pdf', '.doc', '.docx', '.ppt', '.pptx'] 
            : ['.jpg', '.jpeg', '.png', '.gif', '.webp'])
        : null
    };

    if (editingField) {
      // Update existing field
      const updatedFields = fields.map(f => f.id === editingField.id ? fieldData : f);
      onChange(updatedFields);
    } else {
      // Add new field
      onChange([...fields, fieldData]);
    }

    setFieldDialogOpen(false);
    setEditingField(null);
  };

  const handleDeleteField = (fieldId) => {
    onChange(fields.filter(f => f.id !== fieldId));
  };

  const handleMoveField = (index, direction) => {
    const newFields = [...fields];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newFields.length) return;
    
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
    // Update order values
    newFields.forEach((f, i) => f.order = i);
    onChange(newFields);
  };

  const handleLoadTemplate = () => {
    const templateFields = DEFAULT_FORM_TEMPLATE.map(field => ({
      ...field,
      id: field.id || uuidv4(),
      label: getLocalizedValue(field.label),
      placeholder: field.placeholder ? getLocalizedValue(field.placeholder) : null,
      helpText: field.helpText ? getLocalizedValue(field.helpText) : null,
      options: field.options ? field.options.map(opt => ({
        value: opt.value,
        label: getLocalizedValue(opt.label)
      })) : null
    }));
    onChange(templateFields);
  };

  const handleLoadFromCriteria = () => {
    if (!scoringCriteria || scoringCriteria.length === 0) return;
    
    const criteriaPrompts = {
      es: {
        'Innovacion': { label: 'Describe la innovacion de tu solucion', placeholder: 'Explica que hace unica tu propuesta, como se diferencia de lo existente...' },
        'Viabilidad': { label: 'Explica la viabilidad de tu proyecto', placeholder: 'Detalla la viabilidad tecnica, comercial y del modelo de negocio...' },
        'Equipo': { label: 'Describe tu equipo y sus capacidades', placeholder: 'Presenta a los miembros clave, su experiencia y roles...' },
        'Impacto': { label: 'Cual es el impacto potencial de tu solucion', placeholder: 'Describe el impacto social, ambiental o economico esperado...' },
        'Escalabilidad': { label: 'Como planeas escalar tu solucion', placeholder: 'Explica tu estrategia de crecimiento y expansion...' },
      },
      en: {
        'Innovation': { label: 'Describe the innovation of your solution', placeholder: 'Explain what makes your proposal unique, how it differs...' },
        'Feasibility': { label: 'Explain the feasibility of your project', placeholder: 'Detail the technical, commercial and business model feasibility...' },
        'Team': { label: 'Describe your team and capabilities', placeholder: 'Present key members, their experience and roles...' },
        'Impact': { label: 'What is the potential impact of your solution', placeholder: 'Describe the expected social, environmental or economic impact...' },
        'Scalability': { label: 'How do you plan to scale your solution', placeholder: 'Explain your growth and expansion strategy...' },
      }
    };

    const baseFields = DEFAULT_FORM_TEMPLATE.map(field => ({
      ...field,
      id: field.id || uuidv4(),
      label: getLocalizedValue(field.label),
      placeholder: field.placeholder ? getLocalizedValue(field.placeholder) : null,
      helpText: field.helpText ? getLocalizedValue(field.helpText) : null,
      options: field.options ? field.options.map(opt => ({
        value: opt.value,
        label: getLocalizedValue(opt.label)
      })) : null
    }));

    const criteriaFields = scoringCriteria
      .filter(c => c.name && c.name.trim())
      .map((criterion, idx) => {
        const known = criteriaPrompts[language]?.[criterion.name];
        const labelText = known?.label || (language === 'es'
          ? `Describe tu propuesta en relacion a: ${criterion.name}`
          : `Describe your proposal regarding: ${criterion.name}`);
        const placeholderText = known?.placeholder || (criterion.description || '');
        
        return {
          id: `criteria_${criterion.name.toLowerCase().replace(/\s+/g, '_')}_${idx}`,
          type: 'text_long',
          label: labelText,
          placeholder: placeholderText,
          helpText: criterion.description || null,
          required: true,
          order: baseFields.length + idx,
        };
      });

    onChange([...baseFields, ...criteriaFields]);
  };

  const handleAddOption = () => {
    if (!newOption.trim()) return;
    setNewFieldForm({
      ...newFieldForm,
      options: [...newFieldForm.options, { value: '', label: newOption.trim() }]
    });
    setNewOption('');
  };

  const handleRemoveOption = (index) => {
    setNewFieldForm({
      ...newFieldForm,
      options: newFieldForm.options.filter((_, i) => i !== index)
    });
  };

  const sortedFields = [...fields].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{t.title}</h3>
          <p className="text-sm text-gray-500">{t.subtitle}</p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            onClick={handleAddField}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="w-4 h-4 mr-1" />
            {t.addField}
          </Button>
        </div>
      </div>

      {/* Load Template Tip */}
      <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4 flex flex-col sm:flex-row items-start gap-3">
        <div className="p-1.5 bg-amber-100 rounded-lg shrink-0 mt-0.5">
          <FileText className="w-4 h-4 text-amber-700" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-amber-900">
            {language === 'es' ? 'Tip: Carga una plantilla predefinida' : 'Tip: Load a predefined template'}
          </p>
          <p className="text-xs text-amber-700 mt-0.5">
            {language === 'es' 
              ? 'Puedes cargar campos base o generar un formulario conectado a tus criterios de evaluacion para que el postulante responda sobre cada criterio.'
              : 'You can load base fields or generate a form connected to your evaluation criteria so applicants respond to each criterion.'}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          {scoringCriteria && scoringCriteria.length > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleLoadFromCriteria}
              className="shrink-0 border-purple-400 text-purple-800 hover:bg-purple-100 font-semibold"
              data-testid="load-from-criteria-btn"
            >
              {language === 'es' ? 'Desde Criterios' : 'From Criteria'}
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleLoadTemplate}
            className="shrink-0 border-amber-400 text-amber-800 hover:bg-amber-100 font-semibold"
          >
            {t.loadTemplate}
          </Button>
        </div>
      </div>

      {/* Fixed Fields Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-sm text-blue-700">
          <strong>{t.fixedFields}:</strong> {language === 'es' 
            ? 'El nombre y email del postulante se capturan automáticamente de su cuenta.' 
            : 'The applicant\'s name and email are automatically captured from their account.'}
        </p>
      </div>

      {/* Fields List */}
      <div className="space-y-2">
        {sortedFields.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
            <Type className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500">{t.noFields}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={handleLoadTemplate}
            >
              {t.loadTemplate}
            </Button>
          </div>
        ) : (
          sortedFields.map((field, index) => {
            const FieldIcon = FIELD_TYPES[field.type]?.icon || Type;
            return (
              <Card key={field.id} className="border-2 hover:border-purple-300 transition-colors">
                <CardContent className="p-3">
                  <div className="flex items-center gap-3">
                    {/* Reorder buttons */}
                    <div className="flex flex-col gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => handleMoveField(index, -1)}
                        disabled={index === 0}
                      >
                        <ArrowUp className="w-3 h-3" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => handleMoveField(index, 1)}
                        disabled={index === sortedFields.length - 1}
                      >
                        <ArrowDown className="w-3 h-3" />
                      </Button>
                    </div>

                    {/* Field info */}
                    <div className="flex items-center gap-2 flex-1">
                      <FieldIcon className="w-5 h-5 text-purple-600" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{getLocalizedValue(field.label)}</span>
                          {field.required && (
                            <Badge variant="outline" className="text-xs bg-red-50 text-red-600 border-red-200">
                              {t.required}
                            </Badge>
                          )}
                        </div>
                        <span className="text-sm text-gray-500">
                          {FIELD_TYPES[field.type]?.label[language] || field.type}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditField(field)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => handleDeleteField(field.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Field Dialog */}
      <Dialog open={fieldDialogOpen} onOpenChange={setFieldDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingField ? t.editField : t.addField}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Field Type */}
            <div className="space-y-2">
              <Label>{t.fieldType}</Label>
              <Select
                value={newFieldForm.type}
                onValueChange={(value) => setNewFieldForm({ ...newFieldForm, type: value, options: [] })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(FIELD_TYPES).map(([type, config]) => (
                    <SelectItem key={type} value={type}>
                      <span className="flex items-center gap-2">
                        <config.icon className="w-4 h-4" />
                        {config.label[language]}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Label */}
            <div className="space-y-2">
              <Label>{t.fieldLabel} *</Label>
              <Input
                value={newFieldForm.label}
                onChange={(e) => setNewFieldForm({ ...newFieldForm, label: e.target.value })}
              />
            </div>

            {/* Help Text */}
            <div className="space-y-2">
              <Label>{t.helpText}</Label>
              <Input
                value={newFieldForm.helpText}
                onChange={(e) => setNewFieldForm({ ...newFieldForm, helpText: e.target.value })}
              />
            </div>

            {/* Options (for select) */}
            {newFieldForm.type === 'select' && (
              <div className="space-y-2">
                <Label>{t.options}</Label>
                <div className="space-y-2">
                  {newFieldForm.options.map((option, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <Input
                        value={option.label}
                        onChange={(e) => {
                          const newOptions = [...newFieldForm.options];
                          newOptions[index].label = e.target.value;
                          setNewFieldForm({ ...newFieldForm, options: newOptions });
                        }}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-500"
                        onClick={() => handleRemoveOption(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Input
                      value={newOption}
                      onChange={(e) => setNewOption(e.target.value)}
                      placeholder={t.optionLabel}
                      className="flex-1"
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddOption())}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAddOption}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Required checkbox */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="required"
                checked={newFieldForm.required}
                onCheckedChange={(checked) => setNewFieldForm({ ...newFieldForm, required: checked })}
              />
              <Label htmlFor="required" className="cursor-pointer">{t.required}</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFieldDialogOpen(false)}>
              {t.cancel}
            </Button>
            <Button
              onClick={handleSaveField}
              disabled={!newFieldForm.label.trim()}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {t.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FormBuilder;
